// Conjunctions — the joiners. Coordinating (e/ed, o, ma, però) and
// subordinating (che, perché, benché, quando, se…), plus the euphonic-d quirk
// that catches everyone. A reference table groups the common ones at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COORD = [
  ["e / ed", "and", "pane e acqua · madre ed Anna"],
  ["o / oppure", "or", "uno o due · ora oppure mai"],
  ["ma", "but", "voglio ma non posso"],
  ["però", "however / but", "è caro, però buono"],
  ["né", "nor", "né tu né io"],
];
const SUBORD = [
  ["che", "that", "credo che sì"],
  ["perché", "because", "perché voglio"],
  ["benché / sebbene", "although", "benché piova"],
  ["quando", "when", "quando arrivi"],
  ["se", "if", "se puoi"],
  ["mentre", "while", "mentre studio"],
  ["affinché", "so that", "affinché tu impari"],
  ["siccome", "since / as", "siccome è tardi"],
];

export function conjunctionsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([es, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid gap-4 mt-6">
      ${block("Coordinating", COORD)}
      ${block("Subordinating", SUBORD)}
    </div>`;

  renderGuideSections(
    app,
    "Conjunctions",
    "Conjunctions glue words, phrases, and clauses together. The coordinating ones join equals; the subordinating ones hang one idea off another — and several of them trigger the congiuntivo (subjunctive).",
    [
      {
        title: "1. The euphonic d: e → ed, o → od",
        body: `
          <p class="text-sage-700 mb-2">To smooth two vowels colliding, a <b>d</b> can be added — most often with <i>e</i>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>e → ed</b> before a word starting with a vowel (especially <i>e-</i>): <i>madre <b>ed</b> Anna</i>, <i>Italia <b>ed</b> Europa</i>.</li>
            <li><b>a → ad</b> the same way: <i>vado <b>ad</b> Ancona</i>, <i><b>ad</b> esempio</i>.</li>
            <li><b>o → od</b> exists but is rare; modern usage mostly keeps plain <i>o</i>: <i>uno o altro</i>.</li>
          </ul>
        `,
      },
      {
        title: "2. ma vs però vs anzi",
        body: `
          <p class="text-sage-700 mb-2">Italian has a few words for <i>but</i>, each with its own flavour.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>ma</b> adds a plain contrast: <i>È caro <b>ma</b> buono.</i></li>
            <li><b>però</b> = however; it can also slide later in the clause: <i>È caro; <b>però</b> lo compro</i> / <i>Lo compro, <b>però</b>.</i></li>
            <li><b>anzi / bensì</b> correct a negative — "not X but rather Y": <i>Non è rosso, <b>bensì</b> blu.</i> · <i>Non mi dispiace, <b>anzi</b>!</i></li>
          </ul>
        `,
      },
      {
        title: "3. Conjunctions that trigger the congiuntivo",
        body: `
          <p class="text-sage-700 mb-2">Some subordinators introduce something not-yet-real, so the following verb goes congiuntivo:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>affinché / perché</b> (so that): <i>Te lo dico <b>affinché</b> tu lo <b>sappia</b>.</i></li>
            <li><b>benché / sebbene</b> (although): <i><b>Benché</b> <b>piova</b>, esco.</i></li>
            <li><b>prima che</b> (before): <i><b>Prima che</b> <b>sia</b> troppo tardi.</i></li>
            <li><b>a meno che / purché</b> (unless / provided that): <i>Vengo, <b>purché</b> tu ci <b>sia</b>.</i></li>
          </ul>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
